import { ApiInstance } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { Button, Divider, PreIcon } from '@advanza/ui'
import AnalyticsFilter from 'components/analytics/AnalyticsFilter'
import AnalyticsList from 'components/analytics/AnalyticsList'
import GenerateInterimInvoiceButton from 'components/billing/GenerateInterimInvoiceButton'
import InvoicesList from 'components/billing/InvoicesList'
import PerformBadDebtProcedureButton from 'components/billing/PerformBadDebtProcedureButton'
import SendToTroyButton from 'components/billing/SendToTroyButton'
import Card from 'components/Card'
import MatchFilter from 'components/matches/MatchFilter'
import MatchList from 'components/matches/MatchList'
import AutoRow from 'components/misc/AutoRow'
import PhoneClicksFilter from 'components/phoneClicks/PhoneClicksFilter'
import PhoneClicksList from 'components/phoneClicks/PhoneClicksList'
import PhoneViewsList from 'components/phoneViews/PhoneViewsList'
import BadResponseTicketsSwitch from 'components/providers/BadResponseTicketsSwitch'
import HiredPercentageStats from 'components/providers/HiredPercentageStats'
import MatchMailData from 'components/providers/MatchMailData'
import MonthlyReportData from 'components/providers/MonthlyReportData'
import OtherCharges from 'components/providers/OtherCharges'
import ReplacementLeadsCard from 'components/providers/replacementLeads/ReplacementLeadsCard'
import SearchEngineCard from 'components/providers/searchEngine/SearchEngineCard'
import UpcomingInvoiceCard from 'components/providers/UpcomingInvoiceCard'
import ReclaimsList from 'components/reclaims/ReclaimsList'
import ReclaimStats from 'components/reclaims/ReclaimStats'
import WebsiteClicksFilter from 'components/websiteClicks/WebsiteClicksFilter'
import WebsiteClicksList from 'components/websiteClicks/WebsiteClicksList'
import { useProvider } from 'hooks/providerHooks'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

interface Props {
    providerId: number
}

const ProviderViewLeads = ({ providerId }: Props) => {
    const { provider, useFranchiseBilling } = useProvider(providerId)
    const filterIdAssociations = `p_${providerId}`
    return (
        <div>
            <Helmet>
                <title>Office - {provider?.business_name}</title>
            </Helmet>
            <Row>
                <Col xs>
                    <Card
                        topRight={
                            <AutoRow>
                                <BadResponseTicketsSwitch entityId={providerId} />
                                {provider?.is_franchise_main && (
                                    <Link
                                        to={`${ApiInstance.getAddress()}office/franchise-report/get/${providerId}`}
                                        target="_blank">
                                        <Button appearance="primary">Export franchise leads</Button>
                                    </Link>
                                )}
                            </AutoRow>
                        }
                        header="Matches"
                        icon="compare_arrows">
                        {provider?.notes && (
                            <MatchFilter
                                filterId={filterIdAssociations}
                                compact
                                hidePagination
                                defaultOptions={{
                                    providerIds: [providerId],
                                    pageSize: 10,
                                    includeConfirmability: true,
                                }}
                            />
                        )}
                        <MatchList
                            filterId={filterIdAssociations}
                            defaultOptions={{
                                providerIds: [providerId],
                                pageSize: 10,
                                includeConfirmability: true,
                            }}
                            showCols={[
                                'service',
                                'firstChoice',
                                'charge',
                                'confirm',
                                'nrMsgs',
                                'created',
                                'userName',
                                'stripes',
                                'reclaim',
                                'leadScore',
                            ]}
                            hideExport={false}
                        />
                    </Card>
                    <Divider m />
                    <ReplacementLeadsCard providerId={providerId} />
                    <Divider m />
                    <Card header="Reclaims">
                        <ReclaimsList
                            filterId="all"
                            defaultOptions={{ providerId: providerId, pageSize: 10 }}
                            showCols={[
                                'reclaim_id',
                                'status',
                                'created',
                                'phoneNumber',
                                'leadName',
                                'action',
                                'notes',
                            ]}
                            showFilters={['leadType', 'status', 'search']}
                            compact
                        />
                        <ReclaimStats
                            filterId={'all'}
                            defaultOptions={{ providerId: providerId }}
                        />
                    </Card>
                    <Divider m />
                    <UpcomingInvoiceCard provider={provider} />
                </Col>
                <Col xs>
                    <Card icon="phone" header="Phone clicks">
                        <PhoneClicksFilter
                            filterId={filterIdAssociations}
                            defaultOptions={{
                                providerIds: [providerId],
                                pageSize: 10,
                            }}
                        />
                        <PhoneClicksList providerId={providerId} filterId={filterIdAssociations} />
                    </Card>
                    <Divider m />
                    <Card icon="visibility" header="Phone views">
                        <PhoneViewsList providerId={providerId} filterId={filterIdAssociations} />
                    </Card>
                    <Divider m />
                    <Card icon="public" header="Website clicks">
                        <WebsiteClicksFilter
                            filterId={filterIdAssociations}
                            defaultOptions={{
                                providerIds: [providerId],
                                pageSize: 10,
                            }}
                        />
                        <WebsiteClicksList
                            providerId={providerId}
                            filterId={filterIdAssociations}
                        />
                    </Card>
                    <Divider m />
                    <HiredPercentageStats providerId={providerId} />
                    <Divider m />
                    <Card header="Analytics" useToggle>
                        <AnalyticsFilter
                            filterId={filterIdAssociations}
                            defaultOptions={{ providerId: providerId }}
                        />
                        <AnalyticsList
                            filterId={filterIdAssociations}
                            showCols={['yearWeek', 'metric', 'count', 'value']}
                        />
                    </Card>
                    <Divider m />
                    <Card header="Match mail data">
                        <MatchMailData providerId={providerId} />
                    </Card>
                    <Divider m />
                    <Card header="Monthly report">
                        <MonthlyReportData providerId={providerId} />
                    </Card>
                    <Divider m />
                    <SearchEngineCard providerId={providerId} />
                </Col>
            </Row>
            <Divider m />
            <Card
                header={
                    <>
                        Invoices{' '}
                        {useFranchiseBilling && (
                            <span
                                style={{
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                    paddingLeft: 10,
                                }}>
                                <PreIcon green icon="groups">
                                    <b style={{ color: 'green' }}>Invoiced via franchise</b>
                                </PreIcon>
                            </span>
                        )}
                    </>
                }>
                <InvoicesList
                    defaultOptions={{
                        providerId,
                        aboutMonth: undefined,
                        order: 'Invoices.invoice_id DESC',
                    }}
                    filterId={filterIdAssociations}
                    showIncassoLink
                />
            </Card>
            <Divider m />
            <Card>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <GenerateInterimInvoiceButton providerId={providerId} />
                    <PerformBadDebtProcedureButton providerId={providerId} />
                    <SendToTroyButton providerId={providerId} />
                    {provider?.paytUrl && (
                        <a rel="noopener noreferrer" target="_blank" href={provider?.paytUrl}>
                            <Button appearance="secondary" iconBefore="open_in_new">
                                Payt
                            </Button>
                        </a>
                    )}
                </div>
            </Card>
            <Divider m />
            <Card icon="euro" header="Other charges">
                <Divider m />
                <OtherCharges providerId={providerId} />
            </Card>
            <Divider xl />
        </div>
    )
}

export default ProviderViewLeads
